import { createSignal } from 'solid-js'

import { getLocale, translate } from '../i18n'
import { dispatchWeightStateChange } from '../runtimeBridge'
import { formatDateTimeLocalValue, parseDateTimeLocalValue } from '../utils/time'
import { formatQuantity, formatQuantityInput, parseQuantity } from '../utils/units'
import { createEditModalState } from './editModalState'
import { unitSystem } from './unitSystemStore'
import {
   DEFAULT_RANGE_FILTER_KEY,
   getRangeFilterConfig,
   getRangeTimestamps,
   RANGE_FILTER_CONFIG,
   type RangeFilterKey,
} from '../utils/timeRange'

export type WeightFilterKey = RangeFilterKey
export type WeightChartMode = 'line' | 'scatter'

export type WeightEntry = {
   weight: number
   timestamp: number
}

export type WeightProfile = {
   heightCm: number | null
   targetWeightKg: number | null
}

type WeightStatus = {
   message: string
   isError: boolean
}

const WEIGHTS_PER_PAGE = 5
const DEFAULT_WEIGHT_FILTER_KEY: WeightFilterKey = DEFAULT_RANGE_FILTER_KEY
const DEFAULT_WEIGHT_CHART_MODE: WeightChartMode = 'line'

const BMI_CATEGORY_STYLE_MAP = {
   underweight: {
      labelKey: 'Underweight',
      classes: ['text-sky-200', 'border-sky-300/60'],
   },
   healthy: {
      labelKey: 'Healthy',
      classes: ['text-emerald-200', 'border-emerald-300/60'],
   },
   overweight: {
      labelKey: 'Overweight',
      classes: ['text-amber-200', 'border-amber-300/60'],
   },
   obese: {
      labelKey: 'Obese',
      classes: ['text-rose-200', 'border-rose-300/60'],
   },
} as const

const [weightEntries, setWeightEntries] = createSignal<WeightEntry[]>([])
const [weightProfile, setWeightProfile] = createSignal<WeightProfile>({
   heightCm: null,
   targetWeightKg: null,
})
const [showWeightTarget, setShowWeightTargetSignal] = createSignal(true)
const [weightFilter, setWeightFilterSignal] = createSignal<WeightFilterKey>(
   DEFAULT_WEIGHT_FILTER_KEY,
)
const [weightPage, setWeightPageSignal] = createSignal(0)
const [weightInputValue, setWeightInputValueSignal] = createSignal('')
const weightEdit = createEditModalState(['weight', 'timestamp'] as const)
const isWeightEditModalOpen = weightEdit.isOpen
const editingWeightIndex = weightEdit.editingIndex
const editingWeightValue = weightEdit.valueAccessor('weight')
const editingWeightTimestampValue = weightEdit.valueAccessor('timestamp')
const weightEditError = weightEdit.error
const setWeightEditError = weightEdit.setError
const [weightChartMode, setWeightChartModeSignal] = createSignal<WeightChartMode>(
   DEFAULT_WEIGHT_CHART_MODE,
)
const [weightStatus, setWeightStatusSignal] = createSignal<WeightStatus>({
   message: '',
   isError: false,
})

/**
 * Normalizes a weight-chart mode to `line` or `scatter`.
 *
 * @param mode - The candidate mode.
 * @returns The normalized chart mode.
 */
function normalizeWeightChartMode(mode: unknown): WeightChartMode {
   return mode === 'scatter' ? 'scatter' : DEFAULT_WEIGHT_CHART_MODE
}

/**
 * Validates a weight entry and rounds its weight to one decimal.
 *
 * @param entry - The raw entry.
 * @returns The weight entry, or null when invalid.
 */
function normalizeWeightEntry(entry: unknown): WeightEntry | null {
   if (!entry || typeof entry !== 'object') {
      return null
   }

   const candidate = entry as Partial<WeightEntry>
   if (
      typeof candidate.weight !== 'number' ||
      !Number.isFinite(candidate.weight) ||
      typeof candidate.timestamp !== 'number' ||
      !Number.isFinite(candidate.timestamp)
   ) {
      return null
   }

   return {
      weight: Number(candidate.weight.toFixed(1)),
      timestamp: candidate.timestamp,
   }
}

/**
 * Normalizes and sorts weight entries newest first.
 *
 * @param entries - The raw entries.
 * @returns The normalized entries.
 */
function normalizeWeightEntries(entries: unknown[]): WeightEntry[] {
   return entries
      .map(entry => normalizeWeightEntry(entry))
      .filter((entry): entry is WeightEntry => entry !== null)
      .sort((a, b) => b.timestamp - a.timestamp)
}

/**
 * Normalizes a weight profile's height and target weight to finite numbers or null.
 *
 * @param profile - The raw profile.
 * @returns The normalized profile.
 */
function normalizeWeightProfile(profile?: Partial<WeightProfile> | null): WeightProfile {
   return {
      heightCm:
         typeof profile?.heightCm === 'number' && Number.isFinite(profile.heightCm) ?
            profile.heightCm
         :  null,
      targetWeightKg:
         (
            typeof profile?.targetWeightKg === 'number' &&
            Number.isFinite(profile.targetWeightKg)
         ) ?
            profile.targetWeightKg
         :  null,
   }
}

/**
 * Dispatches a runtime event describing the latest weight-state change.
 *
 * @param action - The action that triggered the change.
 */
function emitWeightStateChange(
   action:
      | 'add-weight'
      | 'update-weight'
      | 'delete-weight'
      | 'set-weight-filter'
      | 'toggle-weight-target'
      | 'update-weight-chart-mode',
) {
   dispatchWeightStateChange({
      action,
      weightHistory: weightEntries(),
      weightFilter: weightFilter(),
      showWeightTarget: showWeightTarget(),
      chartMode: weightChartMode(),
   })
}

/**
 * Returns the local-midnight timestamp for a given time.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns Epoch ms at local midnight.
 */
function getDayStartTimestamp(timestamp: number) {
   const date = new Date(timestamp)
   date.setHours(0, 0, 0, 0)
   return date.getTime()
}

/**
 * Finds the index of the weight entry recorded on a given day.
 *
 * @param dayStartTs - The local-midnight timestamp.
 * @returns The entry index, or -1 when none.
 */
function findWeightEntryIndexForDay(dayStartTs: number) {
   return weightEntries().findIndex(
      entry => getDayStartTimestamp(entry.timestamp) === dayStartTs,
   )
}

/**
 * Applies weight history, profile, target visibility, and chart mode from the legacy app layer.
 *
 * @param payload - Partial weight state to apply.
 */
export function syncWeightStateFromLegacy(payload: {
   weightHistory?: unknown[]
   userProfile?: Partial<WeightProfile> | null
   showTarget?: boolean
   chartMode?: unknown
}) {
   if (Array.isArray(payload.weightHistory)) {
      setWeightEntries(normalizeWeightEntries(payload.weightHistory))
   }

   if (payload.userProfile) {
      setWeightProfile(normalizeWeightProfile(payload.userProfile))
   }

   if (typeof payload.showTarget === 'boolean') {
      setShowWeightTargetSignal(payload.showTarget)
   }

   if (payload.chartMode !== undefined) {
      setWeightChartModeSignal(normalizeWeightChartMode(payload.chartMode))
   }
}

/**
 * Clears the weight status message.
 */
function clearWeightStatus() {
   setWeightStatusSignal({ message: '', isError: false })
}

/**
 * Sets the weight input value, clearing any error status.
 *
 * @param value - The raw input value.
 */
export function setWeightInputValue(value: string) {
   setWeightInputValueSignal(value)
   if (weightStatus().isError) {
      clearWeightStatus()
   }
}

/**
 * Sets the active weight time filter and resets pagination.
 *
 * @param period - The filter key.
 */
export function setWeightFilter(period: WeightFilterKey) {
   if (!RANGE_FILTER_CONFIG[period]) {
      return
   }
   setWeightFilterSignal(period)
   setWeightPageSignal(0)
   emitWeightStateChange('set-weight-filter')
}

/**
 * Sets the weight-list page (clamped to a non-negative index).
 *
 * @param page - The zero-based page index.
 */
export function setWeightPage(page: number) {
   setWeightPageSignal(Math.max(0, page))
}

/**
 * Parses a weight typed in the active unit system into kg, rounded to one decimal.
 *
 * @param input - The raw input string.
 * @returns The weight in kg, or null when not a positive number.
 */
function parseWeightInput(input: string): number | null {
   const kg = parseQuantity('mass', input, unitSystem())
   if (kg === null || kg <= 0) {
      return null
   }
   return Number(kg.toFixed(1))
}

/**
 * Formats a weight in kg for display in the active unit system (e.g. `72.4 kg`).
 *
 * @param weightKg - The weight in kg.
 * @returns The formatted string with unit.
 */
export function formatWeight(weightKg: number) {
   return formatQuantity('mass', weightKg, unitSystem(), { locale: getLocale() })
}

/**
 * Opens the edit modal for a weight entry, pre-filling its value and timestamp.
 *
 * @param index - The entry index to edit.
 */
export function openWeightEditModal(index: number) {
   const entry = weightEntries()[index]
   if (!entry) {
      return
   }

   weightEdit.open(index, {
      weight: formatQuantityInput('mass', entry.weight, unitSystem()),
      timestamp: formatDateTimeLocalValue(entry.timestamp),
   })
}

export const closeWeightEditModal = weightEdit.close
export const setEditingWeightInputValue = (value: string) =>
   weightEdit.setValue('weight', value)
export const setEditingWeightTimestampInputValue = (value: string) =>
   weightEdit.setValue('timestamp', value)

/**
 * Validates and saves the edited weight entry (value and timestamp).
 *
 * @returns True when saved, false on validation failure.
 */
export function saveEditedWeightEntry() {
   const index = editingWeightIndex()
   const entry = index !== null ? weightEntries()[index] : undefined
   if (index === null || !entry) {
      setWeightEditError(translate('Selected weight entry is no longer available.'))
      return false
   }

   const normalizedWeight = parseWeightInput(editingWeightValue())
   if (normalizedWeight === null) {
      setWeightEditError(translate('Enter a valid weight above 0.'))
      return false
   }

   const timestamp = parseDateTimeLocalValue(editingWeightTimestampValue())
   if (timestamp === null) {
      setWeightEditError(translate('Enter a valid date and time.'))
      return false
   }
   if (timestamp > Date.now()) {
      setWeightEditError(translate('Weight entries cannot be in the future.'))
      return false
   }

   // One entry per day: moving onto a day that already has a different entry is rejected.
   const dayIndex = findWeightEntryIndexForDay(getDayStartTimestamp(timestamp))
   if (dayIndex !== -1 && dayIndex !== index) {
      setWeightEditError(translate('Another entry already exists on that day.'))
      return false
   }

   if (
      Math.abs(entry.weight - normalizedWeight) < 0.05 &&
      entry.timestamp === timestamp
   ) {
      setWeightStatusSignal({
         message: translate('No changes detected for this entry.'),
         isError: false,
      })
      closeWeightEditModal()
      return true
   }

   const nextEntries = [...weightEntries()]
   nextEntries[index] = { weight: normalizedWeight, timestamp }
   setWeightEntries(normalizeWeightEntries(nextEntries))
   setWeightStatusSignal({
      message: translate('Updated entry to {weight}.', {
         weight: formatWeight(normalizedWeight),
      }),
      isError: false,
   })
   closeWeightEditModal()
   emitWeightStateChange('update-weight')
   return true
}

/**
 * Deletes a weight entry by index and updates the status message.
 *
 * @param index - The entry index to delete.
 */
export function deleteWeightEntry(index: number) {
   if (index < 0 || index >= weightEntries().length) {
      return
   }

   const nextEntries = [...weightEntries()]
   const [removedEntry] = nextEntries.splice(index, 1)
   if (!removedEntry) {
      return
   }

   setWeightEntries(normalizeWeightEntries(nextEntries))
   if (isWeightEditModalOpen()) {
      closeWeightEditModal()
   }
   setWeightStatusSignal({
      message: translate('Deleted entry from {timestamp}.', {
         timestamp: formatWeightTimestamp(removedEntry.timestamp),
      }),
      isError: false,
   })
   emitWeightStateChange('delete-weight')
}

/**
 * Deletes the weight entry open in the edit modal and closes the modal.
 */
export function deleteEditedWeightEntry() {
   const index = editingWeightIndex()
   closeWeightEditModal()
   if (index !== null) {
      deleteWeightEntry(index)
   }
}

/**
 * Adds a weight entry from the input, replacing today's entry when one exists.
 *
 * @returns True when saved, false on validation failure.
 */
export function submitWeightEntry() {
   const normalizedWeight = parseWeightInput(weightInputValue())
   if (normalizedWeight === null) {
      setWeightStatusSignal({
         message: translate('Enter a valid weight above 0.'),
         isError: true,
      })
      return false
   }

   const timestamp = Date.now()
   const dayStart = getDayStartTimestamp(timestamp)
   const existingIndex = findWeightEntryIndexForDay(dayStart)
   const nextEntries = [...weightEntries()]

   if (existingIndex !== -1) {
      nextEntries[existingIndex] = {
         weight: normalizedWeight,
         timestamp,
      }
      setWeightEntries(normalizeWeightEntries(nextEntries))
      setWeightPageSignal(0)
      setWeightStatusSignal({
         message: translate("Updated today's weight to {weight}.", {
            weight: formatWeight(normalizedWeight),
         }),
         isError: false,
      })
      setWeightInputValueSignal('')
      emitWeightStateChange('update-weight')
      return true
   }

   nextEntries.unshift({
      weight: normalizedWeight,
      timestamp,
   })
   setWeightEntries(normalizeWeightEntries(nextEntries))
   setWeightPageSignal(0)
   setWeightStatusSignal({
      message: translate('Logged {weight}.', {
         weight: formatWeight(normalizedWeight),
      }),
      isError: false,
   })
   setWeightInputValueSignal('')
   emitWeightStateChange('add-weight')
   return true
}

/**
 * Toggles display of the weight target line and broadcasts the change.
 *
 * @param nextValue - Whether to show the target.
 */
export function setShowWeightTarget(nextValue: boolean) {
   setShowWeightTargetSignal(nextValue)
   emitWeightStateChange('toggle-weight-target')
}

/**
 * Sets the weight chart mode, optionally broadcasting the change.
 *
 * @param nextMode - The new chart mode.
 * @param options - Set `emit` to false to skip broadcasting.
 */
export function setWeightChartMode(
   nextMode: WeightChartMode,
   options?: { emit?: boolean },
) {
   const normalizedMode = normalizeWeightChartMode(nextMode)
   setWeightChartModeSignal(normalizedMode)
   if (options?.emit !== false) {
      emitWeightStateChange('update-weight-chart-mode')
   }
}

/**
 * Formats a timestamp as a short localized date-time for weight rows.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The localized string.
 */
export function formatWeightTimestamp(timestamp: number) {
   return new Date(timestamp).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
   })
}

/**
 * Returns the translated label for a weight filter key.
 *
 * @param key - The filter key.
 * @returns The localized label.
 */
export function getWeightFilterLabel(key: WeightFilterKey) {
   return translate(getRangeFilterConfig(key).labelKey)
}

/**
 * Computes the start/end time range for a weight filter, expanding to all data for `max`.
 *
 * @param filterKey - The filter key.
 * @param allEntries - All weight entries (used for the `max` range).
 * @returns The range start and end timestamps.
 */
export function getWeightRangeTimestamps(
   filterKey: WeightFilterKey,
   allEntries: WeightEntry[],
) {
   return getRangeTimestamps(
      filterKey,
      allEntries.map(entry => entry.timestamp),
   )
}

/**
 * Filters weight entries to those within a filter's time range.
 *
 * @param entries - The weight entries.
 * @param filterKey - The filter key.
 * @returns The entries within range.
 */
export function getFilteredWeightEntries(
   entries: WeightEntry[],
   filterKey: WeightFilterKey,
) {
   const { rangeStart, rangeEnd } = getWeightRangeTimestamps(filterKey, entries)
   return entries.filter(
      entry => entry.timestamp >= rangeStart && entry.timestamp <= rangeEnd,
   )
}

/**
 * Returns the profile height in meters, or null when unset.
 *
 * @param profile - The weight profile.
 * @returns Height in meters, or null.
 */
export function getHeightMeters(profile: WeightProfile) {
   return typeof profile.heightCm === 'number' && profile.heightCm > 0 ?
         profile.heightCm / 100
      :  null
}

/**
 * Returns the BMI category style bucket for a BMI value.
 *
 * @param bmi - The BMI value.
 * @returns The category style, or null when not finite.
 */
export function getBmiCategory(bmi: number) {
   if (!Number.isFinite(bmi)) {
      return null
   }
   if (bmi < 18.5) {
      return BMI_CATEGORY_STYLE_MAP.underweight
   }
   if (bmi < 25) {
      return BMI_CATEGORY_STYLE_MAP.healthy
   }
   if (bmi < 30) {
      return BMI_CATEGORY_STYLE_MAP.overweight
   }
   return BMI_CATEGORY_STYLE_MAP.obese
}

/**
 * Formats a day count as a localized relative duration (e.g. `in 3 weeks`).
 *
 * @param days - The signed number of days.
 * @returns The localized string, or null when not finite.
 */
export function formatRelativeDurationFromDays(days: number) {
   if (!Number.isFinite(days)) {
      return null
   }

   if (typeof Intl === 'undefined' || typeof Intl.RelativeTimeFormat !== 'function') {
      const rounded = Math.round(Math.abs(days))
      if (rounded === 0) {
         return 'today'
      }
      return days >= 0 ? `in ${rounded} days` : `${rounded} days ago`
   }

   const absDays = Math.abs(days)
   let unit: Intl.RelativeTimeFormatUnit = 'day'
   let value = days
   if (absDays >= 365) {
      unit = 'year'
      value = days / 365
   } else if (absDays >= 60) {
      unit = 'month'
      value = days / 30
   } else if (absDays >= 14) {
      unit = 'week'
      value = days / 7
   }

   return new Intl.RelativeTimeFormat(undefined, {
      numeric: 'always',
   }).format(Math.round(value), unit)
}

/**
 * Formats a projected date for display.
 *
 * @param timestamp - The epoch-ms timestamp, or null.
 * @returns The localized date, or null when invalid.
 */
export function formatProjectionDate(timestamp: number | null) {
   if (timestamp === null || !Number.isFinite(timestamp)) {
      return null
   }
   return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
   })
}

export {
   editingWeightTimestampValue,
   editingWeightValue,
   isWeightEditModalOpen,
   showWeightTarget,
   weightEditError,
   weightChartMode,
   weightEntries,
   weightFilter,
   weightInputValue,
   weightPage,
   weightProfile,
   weightStatus,
   WEIGHTS_PER_PAGE,
}
